import logger from '~/services/logger';
import type { PathParams } from '~/services/pipes/utils.ts';

/**
 * Deterministic sync hash for browser+server environments.
 * We only need stability (not cryptographic strength) for cache keys.
 */
function fnv1a64Hex(input: string): string {
  const FNV_OFFSET_BASIS_64 = 0xcbf29ce484222325n;
  const FNV_PRIME_64 = 0x100000001b3n;
  const MOD_64 = 0xffffffffffffffffn;

  let hash = FNV_OFFSET_BASIS_64;

  for (let i = 0; i < input.length; i += 1) {
    hash ^= BigInt(input.charCodeAt(i));
    hash = (hash * FNV_PRIME_64) & MOD_64;
  }

  return hash.toString(16).padStart(16, '0').substring(0, 16);
}

/**
 * Serialize usable path params for the css-vars stylesheet query.
 * Skips null/undefined/empty values so single-id screens stay compact.
 */
export function serializePathParamsForCssVars(
  pathParams: PathParams | null | undefined
): string {
  if (!pathParams) {
    return '';
  }

  const usableEntries: [string, string][] = Object.entries(pathParams).flatMap(
    ([key, value]) => {
      if (value == null || value === '') {
        return [];
      }

      return [[key, String(value)]];
    }
  );

  if (usableEntries.length === 0) {
    return '';
  }

  return encodeURIComponent(JSON.stringify(Object.fromEntries(usableEntries)));
}

/**
 * Parse path params from the css-vars stylesheet query.
 */
export function parsePathParamsFromCssVars(
  raw: string | null
): PathParams | undefined {
  if (!raw) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return undefined;
    }

    return parsed as PathParams;
  } catch {
    return undefined;
  }
}

/**
 * Stable bounded hash of usable path params for Redis CSS cache keys.
 * Sorted keys keep the hash order-independent; output is bounded hex length.
 */
export function getPathParamsCacheKeyHash(
  pathParams: PathParams | null | undefined
): string | null {
  try {
    if (!pathParams) {
      return null;
    }

    const usableEntries: [string, string][] = Object.entries(pathParams)
      .flatMap(([key, value]): [string, string][] => {
        if (value == null || value === '') {
          return [];
        }

        return [[key, String(value)]];
      })
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB));

    if (usableEntries.length === 0) {
      return null;
    }

    const stablePayload: string = JSON.stringify(
      Object.fromEntries(usableEntries)
    );

    return fnv1a64Hex(stablePayload);
  } catch (error: any) {
    logger.error(`Error in getPathParamsCacheKeyHash: ${error.message}`);
    return null;
  }
}

/**
 * Extracts metadata from the input data object if the screen.id property exists.
 * @param data - The input data object from remix loader.
 * @param screenIdOverride - Optional override for the screen ID.
 * @returns An array that contains the metadata and a link object if they exists.
 */
export function cssVarsMetaFunctionData(data: any, screenIdOverride?: string) {
  try {
    const screenId = screenIdOverride || data?.screen?.id;

    if (!screenId) throw new Error('Screen ID is missing in the input data');

    const hash = data?.hash;

    if (!hash) throw new Error('Hash is missing in the input data');

    const encodedScreenId = encodeURIComponent(screenId);

    const encodedHash = encodeURIComponent(hash);

    const dynamicIdFallback = data?.dynamicIdFallback;

    const dynamicIdFallbackParam = dynamicIdFallback
      ? `&dynamicIdFallback=${encodeURIComponent(dynamicIdFallback)}`
      : '';

    const serializedPathParams = serializePathParamsForCssVars(
      data?.pathParams
    );

    const pathParamsParam = serializedPathParams
      ? `&pathParams=${serializedPathParams}`
      : '';

    const layoutIdOverride = data?.layoutIdOverride;

    const layoutIdOverrideParam = layoutIdOverride
      ? `&layoutId=${encodeURIComponent(layoutIdOverride)}`
      : '';

    const href = `/css-vars-v2.css?screenId=${encodedScreenId}&hash=${encodedHash}${dynamicIdFallbackParam}${pathParamsParam}${layoutIdOverrideParam}`;

    return [
      {
        tagName: 'link',
        rel: 'stylesheet',
        href,
        as: 'style',
      },
    ];
  } catch (error: any) {
    logger.error(`####### cssVarsMetaFunctionData: ${error.message}`);
    return [];
  }
}

/**
 * Extracts additional search screen ID from the input data object.
 * Used for loading CSS vars for the search screen and the search fallback screen.
 * @param data - The input data object from remix loader.
 * @returns The additional search screen ID if it exists, otherwise undefined.
 */
export function getAdditionalSearchScreenId(data: any): string | undefined {
  const searchScreen = data?.screens?.find(
    (screen: any) => screen.type === 'search_v2'
  );

  const screenId = data?.screen?.id;
  const searchScreenId = searchScreen?.id;
  const fallbackSearchScreenId = searchScreen?.general?.fallback;

  if (screenId === fallbackSearchScreenId) return searchScreenId;
  return fallbackSearchScreenId;
}
